import { enqueueSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { formatDateTimeWithoutTimezone } from '../../utils/formatTime';
import { objectToFormData } from '../utils';

export async function getReports() {
  const response = await axios.get('/Report/Reports');
  return response;
}

export async function getReportOptions(reportId) {
  const response = await axios.post(`/Report/GetReportTypes?reportId=${reportId}`);
  return response;
}

async function getReportData(url, download, data, sendAsEmail) {
  let response;
  if (download) {
    if (sendAsEmail) {
      data.sendAsEmail = true;
      response = await axios.post(`/Report/${url}`, objectToFormData(data));
    } else response = await axios.post(`/Report/${url}`, objectToFormData(data), { responseType: 'blob' });
    return response;
  }

  try {
    response = await axios.post(`/Report/${url}`, objectToFormData(data), { timeout: 10000 }); //10000 });
  } catch (error) {
    if (error === 'Timeout') response = error;
    else enqueueSnackbar('Something has gone wrong', { variant: 'error' });
  }
  return response;
}

export async function getOrDownloadItemsOrderedReport(formValues, allItems, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((v) => v.value),
    subcategories: formValues.subcategories?.map((f) => f.id),
    categories: formValues.categories?.map((f) => f.value),
    useBudgetDate: formValues.useBudgetDate,
    allItems,
  };

  return getReportData(`${download ? 'Download' : ''}ItemsOrderedReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadRentalSerialNumberReport(formValues, download) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues?.facilities?.map((f) => f.id),
    serialNumbers: formValues?.serialNumbers,
    vendorId: formValues?.rentalVendor?.value,
  };
  const response = getReportData(
    `${download ? 'Download' : ''}RentalSerialNumberReport`,
    download,
    formData,
    false,
    false
  );
  return response;
}

export async function downloadOperationsReport(formValues) {
  const formData = objectToFormData({
    excludeTicketData: formValues.excludeTicketData ?? false,
    includeFacsWithoutGroup: formValues.includeFacsWithoutGroup ?? false,
    includeChildFacs: formValues.includeChildFacs ?? false,
  });
  const response = await axios.post('/Report/OperationsReport', formData);
  return response;
}
export async function downloadBudgetSummaryReport(formValues) {
  const formData = objectToFormData({
    useBudgetDate: formValues.useBudgetDate,
    category: Array.isArray(formValues.categories) ? formValues.categories[0]?.id : 0,
    companies: formValues.companies?.map((f) => f.id),
    facilities: formValues.facilities?.map((f) => f.id),
    subcategories: formValues.subcategories?.map((f) => f.id),
    month: Array.isArray(formValues.month) ? formValues.month[0].value : formValues.month ?? 0,
    year: Array.isArray(formValues.year) ? formValues.year[0].value : formValues.year ?? 0,
  });
  for (let index = 0; index < formValues.timeFrameFilters.length; ++index) {
    const timeFrame = formValues.timeFrameFilters[index];
    if (timeFrame) {
      formData.append(`timeFrameFilters[${index}].Id`, timeFrame?.value);
      formData.append(`timeFrameFilters[${index}].Name`, timeFrame?.label);
    }
  }
  const response = await axios.post('/Report/BudgetSummaryReport', formData);
  return response;
}

export async function getOrDownloadUsersReport(formValues, download, sendAsEmail) {
  return getReportData(
    `${download ? 'Download' : ''}UsersReport`,
    download,
    { facilities: formValues.facilities?.map((f) => f.id) },
    sendAsEmail
  );
}

export async function getOrDownloadFacilityComparisonReport(formValues, download, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    categories: formValues.categories?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(`${download ? 'Download' : ''}FacilityComparisonReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadVendorComparisonReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    vendors: formValues.vendors?.map((f) => f.value),
    categories: formValues.categories?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(`${download ? 'Download' : ''}VendorComparisonReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadBidSheetReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    vendors: formValues.vendors?.map((f) => f.value),
  };

  return getReportData(`${download ? 'Download' : ''}BidSheetReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadTopLevelBudgetReport(formValues, download, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    companies: formValues.companies?.map((f) => f.value),
    facilities: formValues.facilities?.map((f) => f.value),
    subcategories: formValues.subcategories?.map((f) => f.value),
    categories: formValues.categories?.map((f) => f.value),
    vendors: formValues.vendors?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(`${download ? 'Download' : ''}TopLevelBudgetReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadFacilityHistoryReport(formValues, download, sendAsEmail) {
  const dateRange = [];
  if (formValues?.dateValues) {
    formValues.dateValues.forEach((range) => {
      range.forEach((date) => {
        const formattedDate = date.format('YYYY-MM-DD');
        if (dateRange?.length > 0) {
          const lastDate = dateRange[dateRange?.length - 1];
          const nextDay = new Date(lastDate);
          nextDay.setDate(nextDay.getDate() + 1);

          const nextDayFormatted = nextDay.toISOString().split('T')[0];

          if (nextDayFormatted === formattedDate) {
            dateRange.pop(); // Remove the last date if it's the day before the current date
          }
        }
        dateRange.push(formattedDate);
      });
    });
  }

  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    categories: formValues.categories?.map((f) => f.value),
    dates: dateRange,
    facilityId: formValues.facilities && formValues.facilities.length > 0 ? formValues.facilities[0].value : 0,
  };

  return getReportData(`${download ? 'Download' : ''}FacilityHistoryReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadPoInvoiceMatchupReports(formValues, download, consolidated, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(
    `${download ? 'Download' : ''}${consolidated ? 'Consolidated' : ''}POInvoiceMatchupReport`,
    download,
    formData,
    sendAsEmail
  );
}

export async function downloadInvoicePDFs(formValues) {
  const formData = objectToFormData({
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((f) => f.value),
    startDate: new Date(formValues.startDate).toISOString(),
    endDate: new Date(formValues.endDate).toISOString(),
  });

  const response = await axios.post('/Report/DownloadInvoicePDFs', formData, { responseType: 'blob' });
  return response;
}
export async function getOrDownloadBudgetsReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facs: formValues.facilities?.map((f) => f.value),
  };

  return getReportData(`${download ? 'Download' : ''}BudgetsReport`, download, formData, sendAsEmail);
}

export async function downloadInvoiceBookedReport(formValues) {
  const formData = objectToFormData({
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((f) => f.value),
    includePDFs: formValues.includePDFs,
    startDate: new Date(formValues.startDate).toISOString(),
    endDate: new Date(formValues.endDate).toISOString(),
  });

  const response = await axios.post('/Report/DownloadInvoiceBookedReport', formData, { responseType: 'blob' });
  return response;
}

export async function getOrDownloadSubstituteReport(formValues, download, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    companies: formValues.companies?.map((f) => f.value),
    categories: formValues.categories?.map((f) => f.value),
    subcategories: formValues.subcategories?.map((f) => f.value),
    facilities: formValues.facilities?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(`${download ? 'Download' : ''}SubstituteReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadFacilitiesInvoiceReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
    pendingOnly: formValues.pendingOnly ?? false,
  };

  return getReportData(
    download ? 'DownloadFacilitiesInvoiceReport' : 'InvoicesReport',
    download,
    formData,
    sendAsEmail
  );
}

export async function getOrDownloadInvoiceIssuesReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };

  return getReportData(`${download ? 'Download' : ''}InvoiceIssuesReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadCheckInvoicePDFsReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    vendors: formValues.vendors?.map((f) => f.value),
    facilityId: formValues.facilities && formValues.facilities.length > 0 ? formValues.facilities[0].value : 0,
    startBetweenDate: formValues.startBetweenDate,
    endBetweenDate: formValues.endBetweenDate,
    take: formValues.take,
  };

  return getReportData(`${download ? 'Download' : ''}CheckInvoicePDFsReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadCensusIntegrationReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    companies: formValues.companies?.map((f) => f.id),
  };

  return getReportData(`${download ? 'Download' : ''}CensusIntegrationReport`, download, formData, sendAsEmail);
}
export async function getOrDownloadPriceChangeReport(formValues, download, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((v) => v.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
    priceChangesOnly: formValues.priceChangesOnly,
  };
  return getReportData(`${download ? 'Download' : ''}PriceChangeReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadRejectedItemsByVendorReport(formValues, download, sendAsEmail) {
  const formData = {
    useBudgetDate: formValues.useBudgetDate,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
    facilities: formValues.facilities?.map((f) => f.value),
    vendors: formValues.vendors?.map((v) => v.value),
  };

  return getReportData(`${download ? 'Download' : ''}RejectedItemsByVendorReport`, download, formData, sendAsEmail);
}
export async function getOrDownloadCensusReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };
  return getReportData(`${download ? 'Download' : ''}CensusReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadTransportRequestReport(formValues, download, sendAsEmail) {
  // Treat the selected calendar day as start/end of day, ignoring the browser's local hour.
  const startOfDay = formValues.startDate ? new Date(formValues.startDate) : null;
  if (startOfDay) startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = formValues.endDate ? new Date(formValues.endDate) : null;
  if (endOfDay) endOfDay.setHours(23, 59, 59, 999);

  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    startDate: formatDateTimeWithoutTimezone(startOfDay),
    endDate: formatDateTimeWithoutTimezone(endOfDay),
    transportStatuses: formValues.transportStatuses?.map((s) => s.value),
  };
  return getReportData(`${download ? 'Download' : ''}TransportRequestReport`, download, formData, sendAsEmail);
}

export async function getOrDownloadTransportTripsByDoctorTypeReport(formValues, download, sendAsEmail) {
  const formData = {
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
    facilities: formValues.facilities?.map((f) => f.value),
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  };
  return getReportData(
    `${download ? 'Download' : ''}TransportTripsByDoctorTypeReport`,
    download,
    formData,
    sendAsEmail
  );
}

export async function getReportSchedules() {
  const response = await axios.get(`/Report/GetReportSchedules`);
  return response;
}

export async function addReportSchedule(formValues, options, scheduleId) {
  const formData = objectToFormData({
    scheduleId,
    scheduleName: formValues.name,
    reportId: formValues.selectedReport.id,
    dateFilterId: formValues.timeFrame?.id,
    frequencyId: formValues.frequency?.id,
    recipients: formValues?.recipients?.map((r) => r?.email ?? r),
    active: formValues?.active,
    options: options.filter((x) => !!x.value),
    ftpSiteId: formValues.ftpSiteId,
    downloadAsCsv: !!(formValues.selectedReport?.supportsCsv && formValues.downloadAsCsv),
  });
  const response = await axios.post('/Report/AddReportSchedule', formData);
  return response.data;
}

export async function updateActiveAPI(scheduleId, active) {
  const formData = objectToFormData({
    scheduleId,
    active,
  });
  const res = await axios.post('/Report/UpdateReportActive', formData);
  return res;
}
export async function addReportRecipientAPI(recipientEmail, scheduleId) {
  const formData = objectToFormData({
    scheduleId,
    recipientEmail,
  });
  const res = await axios.post('/Report/AddRecipient', formData);
  return res;
}

export async function deleteReportScheduleAPI(scheduleId) {
  const formData = objectToFormData({ scheduleId });
  const res = await axios.post('/Report/DeleteReportSchedule', formData);
  return res;
}

export async function deleteReportRecipientAPI(recipientId) {
  const formData = objectToFormData({
    recipientId,
  });
  const res = await axios.post('/Report/DeleteRecipient', formData);
  return res;
}
export async function getRecipientEmails() {
  const res = await axios.get('/Report/GetRecipientEmails');
  return res;
}
